import { Card, Paper, Typography } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import Button from "@mui/material/Button";
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { applyPromoCodeDiscount } from "@utils/charges";
import React from "react";
import { ui } from "../../../../../../../../interfaces/interfaces";
import { handleChange, handleChangesType } from './discountsLogic';
import { useDiscounts } from "./useDiscounts";

type Props = {
  parent: string;
}

/**
 * Discounts component to handle the rendering of discount-related UI elements.
 * @param {Props} props - The props for the Discounts component.
 * @returns {JSX.Element} The rendered Discounts component.
 */
const Discounts: React.FC<Props> = ({ parent }) => {
  const {
    prefix,
    charge,
    rateReason,
    rateDiscount,
    promoCode,
    promoCodeApplied,
    promoCodeValid,
    isCapped,
    isFixedPrice,
    priceCap,
    discount,
    discountOptions,

  } = useDiscounts(parent);

  return (
    (<Stack sx={{ px: 1 }}>
      {/* Rate reduction should only be shown for Rate based jobs. CC discounts are applied automatically behind the scenes */}
      {!discount && !isFixedPrice && (
        <Paper elevation={4} sx={(theme) => ({
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(1),
          marginLeft: 2,
          marginRight: 2,
          padding: theme.spacing(2),
          [theme.breakpoints.up(600 + Number(theme.spacing(3)) * 2)]: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
          },
        })}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 12
              }}>
              <Autocomplete
                id="combo-box-planTypes"
                options={discountOptions}
                getOptionLabel={(o) => String(o.discountDescription)}
                isOptionEqualToValue={(o, v) => o.discountId === v.discountId}
                onChange={handleChangesType}
                value={rateReason?.discountDescription ? rateReason : null}
                renderInput={(params) => <TextField required {...params} label="Rate Reduction Reason" name={`${prefix}RateReason`} />}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 12
              }}>
              {rateReason?.discountDescription && (
                <TextField
                  required
                  label={`Min applicable rate £${charge?.maximumReducedRate && Math.round(charge?.maximumReducedRate)}`}
                  name={`${prefix}RateDiscount`}
                  value={rateDiscount}
                  type="number"
                  onChange={(e) => handleChange(e, charge)}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
      {(!rateReason?.discountDescription || discountOptions.map(discount => discount.discountId).includes(rateReason.discountId || 0)) && (
        <Paper elevation={4} sx={(theme) => ({
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(1),
          marginLeft: 2,
          marginRight: 2,
          padding: theme.spacing(2),
          [theme.breakpoints.up(600 + Number(theme.spacing(3)) * 2)]: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
          },
        })}>
          <Grid container spacing={2} alignItems="center">
            <Grid
              size={{
                xs: 4,
                sm: 4
              }}>
              <TextField
                required
                label={`Promo code`}
                name={`PromoCode`}
                value={promoCode}
                type="text"
                onChange={(e) => { ui.PromoCode = e.target.value }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2,
                sm: 2
              }}>
              <Button
                variant="contained"
                disabled={(!promoCodeValid || promoCodeApplied)}
                onClick={applyPromoCodeDiscount}
              >
                Apply
              </Button>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 6
              }}>
              {discount && (
                <Card sx={{ minWidth: 200, p: 1, backgroundColor: "#00800017" }}>
                  {`Discount: ${discount.discountDescription}`}
                </Card>
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 12
              }}>
              {discount && (
                <Typography sx={{ minWidth: 200, p: 1, textAlign: "centre" }}>
                  {`Discount: ${discount.isPercentage ? "" : "£"}${discount.discount}${discount.isPercentage ? "%" : ""}${isCapped ? " capped at £" + priceCap : ""}`}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Stack>)
  );
}

export default React.memo(Discounts);